import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useArticle, useUpdateArticle, useArticleRecordings } from '@/queries/articles';
import { useAuth } from '@/contexts/AuthContext';
import { ArticleEditor } from '@/components/ArticleEditor';
import { TopicSelect } from '@/components/TopicSelect';
import { TagInput } from '@/components/TagInput';
import { RecordingsList } from '@/components/RecordingsList';
import { AddRecordingModal } from '@/components/AddRecordingModal';
import { CircularLoader } from '@/components/CircularLoader';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import HeadphonesIcon from '@/assets/common/headphones.svg?react';
import PlusIcon from '@/assets/common/plus.svg?react';
import styles from './create.module.scss';

export const Route = createFileRoute('/articles/$articleId/edit')({
  component: EditArticlePage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function EditArticlePage() {
  const { articleId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: article, isLoading: articleLoading } = useArticle(articleId);
  const { data: recordings } = useArticleRecordings(articleId);
  const updateArticle = useUpdateArticle();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [topic, setTopic] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [showAddRecordingModal, setShowAddRecordingModal] = useState(false);

  // Load article data when it's available
  useEffect(() => {
    if (article) {
      setTitle(article.title);
      setContent(article.content);
      setTopic(article.topic);
      setTags(article.tags || []);
      setIsPublic(article.is_public);
    }
  }, [article]);

  // Check if user is the owner
  const isOwner = user?.uid === article?.user_id;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    updateArticle.mutate(
      {
        id: articleId,
        data: {
          title: title.trim(),
          content,
          topic,
          tags,
          is_public: isPublic,
        },
      },
      {
        onSuccess: () => {
          navigate({
            to: '/articles/$articleId',
            params: { articleId },
          });
        },
      }
    );
  };

  if (articleLoading) {
    return (
      <div className={styles.createArticle}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <CircularLoader />
        </div>
      </div>
    );
  }

  if (!article || !isOwner) {
    return (
      <div className={styles.createArticle}>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <p>Article not found or you don't have permission to edit it.</p>
          <button onClick={() => navigate({ to: '/articles' })}>
            Return to Articles
          </button>
        </div>
      </div>
    );
  }

  const isValid = title.trim().length > 0 && content.trim().length > 0;

  return (
    <div className={styles.createArticle}>
      <div className={styles.createArticle__header}>
        <button
          onClick={() => navigate({ to: '/articles/$articleId', params: { articleId } })}
          className={styles.createArticle__backButton}
        >
          <ArrowLeftIcon />
          Back to Article
        </button>
        <h1 className={styles.createArticle__title}>Edit Article</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.createArticle__form}>
        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__label}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter article title..."
            className={styles.createArticle__input}
          />
        </div>

        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__label}>Content</label>
          <ArticleEditor value={content} onChange={setContent} />
        </div>

        <div className={styles.createArticle__row}>
          <div className={styles.createArticle__field}>
            <label className={styles.createArticle__label}>Topic</label>
            <TopicSelect value={topic} onChange={setTopic} />
          </div>

          <div className={styles.createArticle__field}>
            <label className={styles.createArticle__label}>Tags</label>
            <TagInput value={tags} onChange={setTags} />
          </div>
        </div>

        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__checkbox}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <span>Make this article public</span>
          </label>
          <p className={styles.createArticle__hint}>
            Public articles can be viewed by anyone and receive comments
          </p>
        </div>

        <div className={styles.createArticle__actions}>
          <button
            type="button"
            onClick={() => navigate({ to: '/articles/$articleId', params: { articleId } })}
            className={styles.createArticle__cancelButton}
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            className={styles.createArticle__submitButton}
            disabled={!isValid || updateArticle.isPending}
            whileHover={isValid ? { scale: 1.02 } : {}}
            whileTap={isValid ? { scale: 0.98 } : {}}
          >
            {updateArticle.isPending ? 'Saving...' : 'Save Changes'}
          </motion.button>
        </div>
      </form>

      {/* Audio Recordings Section */}
      <div className={styles.createArticle__recordingsSection}>
        <div className={styles.createArticle__recordingsHeader}>
          <div className={styles.createArticle__recordingsTitle}>
            <HeadphonesIcon />
            <h2>Audio Recordings</h2>
            {recordings && recordings.length > 0 && (
              <span className={styles.createArticle__recordingsCount}>
                {recordings.length}
              </span>
            )}
          </div>
          <motion.button
            type="button"
            onClick={() => setShowAddRecordingModal(true)}
            className={styles.createArticle__addRecordingButton}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PlusIcon />
            Add Recording
          </motion.button>
        </div>

        <RecordingsList articleId={articleId} isOwner={true} />
      </div>

      <AddRecordingModal
        articleId={articleId}
        isOpen={showAddRecordingModal}
        onClose={() => setShowAddRecordingModal(false)}
      />
    </div>
  );
}
